/**
 * AdminLayout — Admin panel shell with sidebar navigation
 * Checks admin access on mount; redirects back if unauthorized.
 */

import { useState, useEffect, type ReactNode } from 'react';
import { useUIStore } from '../../store';
import { checkAdminAccess } from '../../services/AdminAPI';
import { Dashboard } from './admin/Dashboard';
import { CardManager } from './admin/CardManager';
import { PackManager } from './admin/PackManager';
import { PlayerBrowser } from './admin/PlayerBrowser';

type AdminTab = 'dashboard' | 'cards' | 'packs' | 'players';

const TABS: { id: AdminTab; label: string; icon: string }[] = [
  { id: 'dashboard', label: '總覽', icon: '📊' },
  { id: 'cards', label: '卡牌管理', icon: '🃏' },
  { id: 'packs', label: '卡包管理', icon: '📦' },
  { id: 'players', label: '玩家查詢', icon: '👤' },
];

export function AdminLayout() {
  const [activeTab, setActiveTab] = useState<AdminTab>('dashboard');
  const [accessState, setAccessState] = useState<'checking' | 'granted' | 'denied'>('checking');
  const { goBack } = useUIStore();

  // Check admin permission on mount
  useEffect(() => {
    void checkAdminAccess().then((isAdmin) => {
      if (isAdmin) {
        setAccessState('granted');
      } else {
        setAccessState('denied');
      }
    }).catch(() => {
      setAccessState('denied');
    });
  }, []);

  // Prevent body scroll
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  if (accessState === 'checking') {
    return (
      <div style={{ ...styles.container, justifyContent: 'center', alignItems: 'center' }}>
        <span style={{ fontSize: '16px', color: '#888888' }}>驗證管理員權限中...</span>
      </div>
    );
  }

  if (accessState === 'denied') {
    return (
      <div style={{ ...styles.container, justifyContent: 'center', alignItems: 'center', flexDirection: 'column', gap: '16px' }}>
        <span style={{ fontSize: '48px' }}>🚫</span>
        <span style={{ fontSize: '18px', color: '#ff4444' }}>無管理員權限</span>
        <button style={styles.backButton} onClick={goBack}>← 返回</button>
      </div>
    );
  }

  const renderContent = (): ReactNode => {
    switch (activeTab) {
      case 'dashboard':
        return <Dashboard />;
      case 'cards':
        return <CardManager />;
      case 'packs':
        return <PackManager />;
      case 'players':
        return <PlayerBrowser />;
    }
  };

  return (
    <div style={styles.container}>
      {/* Sidebar */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarHeader}>
          <h2 style={styles.sidebarTitle}>⚙️ 管理後台</h2>
          <button style={styles.backButton} onClick={goBack}>
            ← 返回
          </button>
        </div>
        <nav style={styles.nav}>
          {TABS.map((tab) => (
            <button
              key={tab.id}
              style={{
                ...styles.navItem,
                backgroundColor: activeTab === tab.id ? '#3a5a8a' : 'transparent',
              }}
              onClick={() => { setActiveTab(tab.id); }}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
            </button>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div style={styles.main}>
        {renderContent()}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    width: '100vw',
    height: '100vh',
    display: 'flex',
    backgroundColor: '#1a1a2e',
    color: '#ffffff',
    fontFamily: 'Arial, sans-serif',
  },
  sidebar: {
    width: '220px',
    backgroundColor: '#16213e',
    display: 'flex',
    flexDirection: 'column',
    borderRight: '1px solid #2a2a4a',
  },
  sidebarHeader: {
    padding: '20px 16px',
    borderBottom: '1px solid #2a2a4a',
  },
  sidebarTitle: {
    margin: '0 0 12px 0',
    fontSize: '18px',
    fontWeight: 'bold',
  },
  backButton: {
    padding: '6px 12px',
    fontSize: '12px',
    backgroundColor: '#3a3a5a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    width: '100%',
  },
  nav: {
    display: 'flex',
    flexDirection: 'column',
    padding: '8px',
    gap: '4px',
  },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '10px 12px',
    fontSize: '14px',
    color: '#ffffff',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    textAlign: 'left',
  },
  main: {
    flex: 1,
    overflow: 'auto',
    padding: '24px',
  },
};
